import { useMemo, useState } from 'react';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useAuthStore } from '../../../stores/authStore';
import { DynamicTable } from '../../../components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '../../../components/custom/table/dynamic-table-types';
import type { SaasProductDto } from '@cms/models';
import { STATUS_OPTIONS } from '@cms/utils';
import { SearchFilter, StatusFilter } from '../../../components/custom/filters';
import { saasApi } from '../../../lib/api/saas.api';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '../../../components/common/AdminTableRowActions';
import { ProductFormModal, type ProductFormModalMode } from './ProductFormModal';

interface ProductsTableProps {
  rows: SaasProductDto[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (product: SaasProductDto) => void;
  onEdit: (product: SaasProductDto) => void;
  onDelete: (product: SaasProductDto) => void;
  isDeleting: boolean;
  isLoading: boolean;
}

function ProductsTable(props: Readonly<ProductsTableProps>) {
  const { rows, controllers, rowOffset, onView, onEdit, onDelete, isDeleting, isLoading } = props;
  const { col, act, page, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<SaasProductDto>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: col('name'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: col('code'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: col('description'),
      columnType: 'text',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 220,
      minSize: 180,
      meta: { className: 'min-w-[180px] text-right' },
    },
    ],
    [act, col, isDeleting, onDelete, onEdit, onView, rowOffset, statusOptions],
  );

  return (
    <DynamicTable<SaasProductDto>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id as string,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('products', 'empty')}
    />
  );
}

type ProductStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

export function ProductsPage() {
  const { col, page, statusOptions, allStatusesOption } = useAdminI18n();
  const tenantId = useAuthStore(s => s.tenantId ?? null);
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'code', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: ProductFormModalMode;
    product: SaasProductDto | null;
  }>({ open: false, mode: 'create', product: null });

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['saas-products', tenantId ?? ''],
    queryFn: () => saasApi.getSaasProducts(tenantId ?? undefined),
  });

  const filteredProducts = useMemo(() => {
    let list = products;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(p => {
        const code = p.code?.toLowerCase() ?? '';
        const name = p.name?.toLowerCase() ?? '';
        const description = p.description?.toLowerCase() ?? '';
        return code.includes(term) || name.includes(term) || description.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(p => (p.status ?? '') === statusFilter);
    }
    return list;
  }, [products, search, statusFilter]);

  const sortedProducts = useMemo(() => {
    if (!sorting.length) return filteredProducts;
    const { id, desc } = sorting[0]!;
    const field = id === 'name' || id === 'code' || id === 'description' ? id : 'code';
    return [...filteredProducts].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field];
      const bv = (b as Record<string, unknown>)[field];
      const as = (av ?? '') as string;
      const bs = (bv ?? '') as string;
      const cmp = as.localeCompare(bs);
      return desc ? -cmp : cmp;
    });
  }, [filteredProducts, sorting]);

  const totalRecords = sortedProducts.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };

  const paginatedProducts = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedProducts.slice(start, start + pageSize);
  }, [sortedProducts, safePageIndex, pageSize]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => saasApi.deleteSaasProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saas-products'] });
    },
  });

  const handleDelete = async (product: SaasProductDto) => {
    if (!globalThis.confirm(page('products', 'delete_confirm', { name: product.name ?? '' }))) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(product.id as string);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : page('products', 'delete_failed');
      alert(message);
    }
  };

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('products', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('products', 'subtitle')}</p>
        </div>
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', product: null })}
        >
          {page('products', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="products-name"
            label={col('name')}
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('products', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="products-status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as ProductStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <ProductsTable
          rows={paginatedProducts}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          onView={p =>
            setFormModal({ open: true, mode: 'view', product: p })
          }
          onEdit={p =>
            setFormModal({ open: true, mode: 'edit', product: p })
          }
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          isLoading={isLoading}
        />
      </div>

      <ProductFormModal
        open={formModal.open}
        onClose={() =>
          setFormModal(prev => ({ ...prev, open: false, product: null }))
        }
        mode={formModal.mode}
        product={formModal.product}
        onSuccess={() =>
          setFormModal(prev => ({ ...prev, open: false, product: null }))
        }
      />
    </div>
  );
}
